import * as React from 'react';
import { EventManager } from '../../../services/EventManager';
import DescriptionWrapper from '../../common/DescriptionWrapper';
import { Message } from '../../common/Message';
import { Card as CardModel, PaymentDetails, PaymentDetails as PaymentDetailsData } from 'fs-api-client';
import { BillingDetailsElement } from './BillingDetailsElement';
import { injectBillingDetails, BillingDetailsInjectorProperties } from '../../hocs/BillingDetailsInjector';
import { PageRoutes } from '../../root/Routes';
import { WebportalEvent } from '../../../services/WebportalEvent';
import { Token, Card as StripeCard } from '@stripe/stripe-js';
import { TaxInfo, TaxStatus } from './interfaces/TaxInfo';
import { pushEventToAnalytics } from 'src/webportal/utils/utilFunctions';

interface BillingProperties extends BillingDetailsInjectorProperties {
    history: any;
}

interface BillingDetailsUpdate {
    isCardCommitInitiated?: boolean;
    errorMessage?: string;
    successMessage?: string;
}

interface BillingState extends BillingDetailsUpdate {
    name: string;
    card?: CardModel;
    hasPendingChanges: boolean;
    saving: boolean;
    isCardDataComplete: boolean;
    isAddressInputValid: boolean;
}

class BillingDetails extends React.Component<BillingProperties, BillingState> {
    constructor(props: BillingProperties) {
        super(props);

        this.handleNameChange = this.handleNameChange.bind(this);
        this.handleCardReset = this.handleCardReset.bind(this);
        this.handleCardDataChange = this.handleCardDataChange.bind(this);
        this.handleAddressDataChange = this.handleAddressDataChange.bind(this);
        this.handleBillingInformationCommit = this.handleBillingInformationCommit.bind(this);
        this.handleSaveClick = this.handleSaveClick.bind(this);
        this.resolveSaveButtonDisabledValue = this.resolveSaveButtonDisabledValue.bind(this);
        this.resolveSaveButtonClassName = this.resolveSaveButtonClassName.bind(this);
        this.getErrorMessage = this.getErrorMessage.bind(this);
        this.setStateAsync = this.setStateAsync.bind(this);

        this.state = resolveInitialState(props);
    }

    componentDidUpdate() {
        EventManager.fireCustomEvent(WebportalEvent.FloatLabels);
    }

    render() {
        const errorElement: JSX.Element | null = this.state.errorMessage ?
            (
                <Message message={this.state.errorMessage} />
            ) :
            null;
        const successElement: JSX.Element | null = this.state.successMessage ?
            (
                <Message 
                    isSuccess={true}
                    title={'Success'}
                    message={this.state.successMessage} 
                    button={{
                        text: 'Review outstanding invoices',
                        href: '/billing'
                    }}
                />
            ) :
            null;

        let message = null;
        if (!this.state.saving) {
            message = errorElement ? errorElement : successElement;
        }

        { /* TODO: Make it so that any page isn't required to know about its parent. */ }
        return (

            <DescriptionWrapper
                title="Billing details"
                description="Please make sure all billing details are accurate and complete"
                previousPageTitle="Billing"
                onPreviousPageLinkClick={() => { this.props.history.push(PageRoutes.Billing); }}
            >
                <div className="col-12 col-lg-7 col-xl-7 offset-lg-1 d-flex align-items-center billing-details">
                    <div className="container p-0">
                        <div className="row">
                            <div className="col-12 col-lg-11 offset-lg-0 col-xl-8 offset-xl-1">
                                {message}
                                <div className="form">
                                    <BillingDetailsElement
                                        billingAddress={this.props.paymentDetails.billingAddress}
                                        cardData={this.state.card}
                                        onReset={this.handleCardReset}
                                        onCardDataChange={this.handleCardDataChange}
                                        isCommitInitiated={this.state.isCardCommitInitiated}
                                        onCommit={this.handleBillingInformationCommit}
                                        billingName={this.props.paymentDetails.name}
                                        description={this.props.paymentDetails.description}
                                        onAddressDataChange={this.handleAddressDataChange}
                                        taxInfo={{
                                            taxId: this.props.paymentDetails.taxCode,
                                            taxStatus: this.props.paymentDetails.taxStatus as TaxStatus
                                        }}
                                    />

                                    <button
                                        onClick={this.handleSaveClick}
                                        disabled={this.resolveSaveButtonDisabledValue()}
                                        className={this.resolveSaveButtonClassName()}
                                    >
                                        <span>Save changes</span>
                                    </button>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DescriptionWrapper>
        );
    }

    private handleNameChange(e: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({ hasPendingChanges: true, name: e.target.value });
    }

    private handleSaveClick(): void {
        this.setState({
            saving: true,
            isCardCommitInitiated: true,
            hasPendingChanges: false,
            errorMessage: undefined,
            successMessage: 'Billing details successfully saved.',
            isAddressInputValid: false
        });
    }

    private handleCardReset(): void {
        this.setState({ card: undefined, isCardDataComplete: false });
    }

    private getErrorMessage(e: string) {
        return `We were unable to update your billing details. ${e}` ;
    }

    private handleCardDataChange(complete: boolean): void {
        this.setState({ hasPendingChanges: true, isCardDataComplete: complete });
    }

    private handleAddressDataChange(isInputValid: boolean): void {
        this.setState({ hasPendingChanges: true, isAddressInputValid: isInputValid });
    }

    private handleBillingInformationCommit(result: { token: any, error: any } | null, taxInfo: TaxInfo): void {
        if (result && result.error) {
            this.completeSave(result.error);
        } else {
            let token = null;
            if (result) {
                token = result.token;
            }
            this.persistToken(token, taxInfo)
                .then(card => {
                    this.completeSave(card);
                    pushEventToAnalytics('payment_info_entered');
                })
                .catch(e => this.completeSave(e));
        }
    }

    private resolveSaveButtonDisabledValue(): boolean {
        const enabled =
            this.state.hasPendingChanges &&
            this.state.isCardDataComplete &&
            this.state.isAddressInputValid &&
            !this.state.saving;
        return !enabled;
    }

    private resolveSaveButtonClassName(): string {
        return 'btn btn-short' + (this.state.saving ? ' loading' : '');
    }

    private async persistToken(token: Token, taxInfo: TaxInfo): Promise<CardModel | undefined> {
        let paymentDetails = new PaymentDetailsData();
        Object.assign(paymentDetails, this.props.paymentDetails);
        paymentDetails.description = this.state.name;
        paymentDetails.paymentSourceToken = undefined;
        if (token && token.card) {
            this.getCardDetailsFromToken(paymentDetails, token);
            this.setBillingAddressData(paymentDetails, token.card);
        }
        await this.setStateAsync({ isCardCommitInitiated: false });
        paymentDetails.taxCode = taxInfo.taxId;
        paymentDetails.taxStatus = taxInfo.taxStatus;
        await this.props.updateBillingDetails(paymentDetails);
        return paymentDetails.card;
    }

    private setBillingAddressData(paymentDetails: PaymentDetails, card: StripeCard ): void {
        paymentDetails.billingAddress = {};
        if (card.address_line1) {
            paymentDetails.billingAddress.line1 = card.address_line1;
        }
        if (card.address_line2) {
            paymentDetails.billingAddress.line2 = card.address_line2;
        }
        if (card.address_state) {
            paymentDetails.billingAddress.state = card.address_state;
        }
        if (card.address_zip) {
            paymentDetails.billingAddress.postalCode = card.address_zip;
        }
        if (card.address_country) {
            paymentDetails.billingAddress.country = card.address_country;
        }
        if (card.address_city) {
            paymentDetails.billingAddress.city = card.address_city;
        }
        if (card.name) {
            paymentDetails.name = card.name;
        }
    }

    private getCardDetailsFromToken(paymentDetails: PaymentDetails, token: any): void {
        paymentDetails.paymentSourceToken = token.id;
        paymentDetails.card = new CardModel();
        paymentDetails.card.numberLast4 = token.card.last4;
        paymentDetails.card.expiryMonth = token.card.exp_month;
        paymentDetails.card.expiryYear = token.card.exp_year;
    }

    private async setStateAsync(billingState: BillingDetailsUpdate): Promise<void> {
        return new Promise((resolve) => {
            this.setState(billingState, resolve);
        });
    }

    private completeSave(cardOrError: any): void {
        const state = { saving: false, isCardCommitInitiated: false } as BillingState;
        if (cardOrError instanceof CardModel) {
            state.card = cardOrError;
        } else if (cardOrError && cardOrError.message) {
            state.errorMessage = this.getErrorMessage(cardOrError.message);
        }
        this.setState(state);
    }

}

function resolveInitialState(props: BillingProperties): BillingState {
    const state: BillingState = {
        name: '',
        hasPendingChanges: false,
        saving: false,
        isCardCommitInitiated: false,
        isCardDataComplete: false,
        errorMessage: props.errorMessage,
        isAddressInputValid: false
    };

    if (props.paymentDetails) {
        if (props.paymentDetails.description) {
            state.name = props.paymentDetails.description;
        }

        if (props.paymentDetails.card) {
            state.card = props.paymentDetails.card;
            state.isCardDataComplete = true;
        }
    }

    return state;
}

export default injectBillingDetails(BillingDetails);



// WEBPACK FOOTER //
// ./src/webportal/components/billing/details/BillingDetails.tsx